import {
  CheckCircle,
  Close,
  CloudDownload,
  RemoveRedEye,
  ThumbDown,
  ThumbUp,
} from "@mui/icons-material";
import { CircularProgress, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { isCashPayment } from "./constants";
import {
  classifyDocUrl,
  applyDecision,
  isSaveComplete,
  isApproveDisabled,
  isDeclineDisabled,
  buildIpdUpdatePayload,
  buildGenericFieldPayload,
  formatToDateTimeLocal,
} from "./pureLogic";
import * as api from "./billCollection.service";
import CollectionUpdation from "./CollectionUpdation";
import CollectionHistory from "./CollectionHistory";

const isViewerOnly = () => localStorage.getItem("viewer") === "true";

const DocumentActionsTab = ({
  data,
  urlArray,
  isDisable,
  isFinalBill,
  onSaveData,
  onRefresh,
  onUpdateIpdNo,
  onUpdateInfo,
}) => {
  const [activeFileTab, setActiveFileTab] = useState("pdf");
  const [activeRightTab, setActiveRightTab] = useState("verification");
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [signedDocs, setSignedDocs] = useState([]);
  const [finalStatusArray, setFinalStatusArray] = useState([]);
  const [finalBillAmount, setFinalBillAmount] = useState(
    data?.finalBillObj?.finalBillAmount ?? null
  );
  const [netBillAmount, setNetBillAmount] = useState(
    data?.finalBillObj?.netBillAmount ?? null
  );
  const [ipdNoOnBill, setIpdNoOnBill] = useState(data?.ipdNoOnBill || "");
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [declineIndex, setDeclineIndex] = useState(null);
  const [declineReason, setDeclineReason] = useState("");

  const [form, setForm] = useState({
    actualOtStartTime: data?.actualOtStartTime || "",
    actualOtEndTime: data?.actualOtEndTime || "",
    discharge_date: data?.discharge_date || "",
    actual_admission_date: data?.actual_admission_date || "",
    totalCollectibleAtAdmission: data?.totalCollectibleAtAdmission || "",
    totalCollectibleAtDischarge: data?.totalCollectibleAtDischarge || "",
    totalCollectedAtAdmission: data?.totalCollectedAtAdmission || "",
    totalCollectedAtDischarge: data?.totalCollectedAtDischarge || "",
  });

  const paymentMode = data?.payment_mode || "";
  const isCash = isCashPayment(paymentMode);
  const appId = data?.appID || "";
  const leadId = data?.leadId || data?.lead_id || "";
  const userId = localStorage.getItem("userId") || "";
  const userName = localStorage.getItem("userName") || "";

  // Initialize signed docs + seed decisions from any existing per-doc statuses
  // (parity with Angular initActionsTab).
  useEffect(() => {
    setSignedDocs(
      (urlArray || []).map((doc) => ({
        ...doc,
        urlSign: null,
        isImage: false,
      }))
    );
    const seeded = [];
    (urlArray || []).forEach((doc, i) => {
      const status = doc?.status || "";
      if (/approve/gi.test(status)) {
        seeded[i] = { _id: doc._id, status: "Approved" };
      } else if (/decline/gi.test(status)) {
        seeded[i] = { _id: doc._id, status: "Declined", declineReason: doc.declineReason };
      }
    });
    setFinalStatusArray(seeded);
    if (urlArray?.length) loadDoc(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlArray]);

  const loadDoc = useCallback(
    async (index) => {
      const doc = (urlArray || [])[index];
      if (!doc?.url) return;
      // Reuse an already-signed URL (idempotent) — do not re-sign.
      if (signedDocs[index]?.urlSign) {
        setCurrentDocIndex(index);
        return;
      }
      setPdfLoading(true);
      try {
        const res = await api.getSignedUrl([doc.url]);
        const signed = res?.result?.[0] || res?.[0] || doc.url;
        const { isImage: isImg, viewerUrl } = classifyDocUrl(doc.url, signed);
        setSignedDocs((prev) => {
          const copy = [...prev];
          copy[index] = { ...copy[index], urlSign: viewerUrl, isImage: isImg };
          return copy;
        });
        setCurrentDocIndex(index);
      } catch {
        toast.error("Failed to load document");
      } finally {
        setPdfLoading(false);
      }
    },
    [urlArray, signedDocs]
  );

  const downloadAttachment = async (item) => {
    try {
      const res = await api.getSignedUrl([item.url]);
      const signedUrl = res?.result?.[0] || res?.[0] || item.url;
      const link = document.createElement("a");
      link.href = signedUrl;
      link.download = item.name || "document";
      link.target = "_blank";
      link.click();
    } catch {
      toast.error("Download failed");
    }
  };

  const onFieldChange = async (fieldName, value) => {
    if (value === null || value === undefined || value === "") return;
    try {
      const payload = buildGenericFieldPayload(
        appId,
        leadId,
        fieldName,
        value,
        userId,
        userName
      );
      const res = await api.updateCentralDashReporting(payload);
      if (res?.status === 200 || res?.status === undefined) {
        toast.success(`${fieldName} updated`);
        onUpdateInfo?.(fieldName);
      }
    } catch {
      toast.error("Update failed");
    }
  };

  const updateIPD = async () => {
    if (!ipdNoOnBill) return;
    try {
      const payload = buildIpdUpdatePayload(appId, ipdNoOnBill, userId);
      await api.updateCentralDashReporting(payload);
      toast.success("IPD updated");
      onRefresh?.();
      onUpdateIpdNo?.(ipdNoOnBill);
    } catch {
      toast.error("IPD update failed");
    }
  };

  const changeStatus = (status, docId, index) => {
    // Viewer-only sessions cannot record decisions.
    if (isViewerOnly()) {
      toast.warning("You are only allowed to view.");
      return;
    }
    if (status === "Approved") {
      if (isCash && finalBillAmount === null) {
        toast.error("Final Bill Amount is required");
        return;
      }
      if (!window.confirm(`Approve this document?`)) return;
      applyStatus(index, { _id: docId, status: "Approved" });
      toast.success("Document Approved");
      if ((urlArray || []).length === 1) handleSaveData();
    } else if (status === "Declined") {
      setDeclineIndex(index);
      setDeclineReason("");
      setDeclineModalOpen(true);
    }
  };

  const confirmDecline = () => {
    if (!declineReason.trim()) {
      toast.info("Please enter a decline reason");
      return;
    }
    const doc = urlArray[declineIndex];
    applyStatus(declineIndex, {
      _id: doc?._id,
      status: "Declined",
      declineReason,
    });
    toast.success("Document Declined");
    setDeclineModalOpen(false);
    if ((urlArray || []).length === 1) handleSaveData();
  };

  const applyStatus = (index, statusObj) => {
    setFinalStatusArray((prev) => applyDecision(prev, index, statusObj));
  };

  const handleSaveData = () => {
    if (isCash && finalBillAmount === null) {
      toast.error("Final Bill Amount is required");
      return;
    }
    if (!isSaveComplete(finalStatusArray, (urlArray || []).length)) {
      toast.info("Please Review All Images");
      return;
    }
    onSaveData?.({
      status: "",
      data: finalStatusArray,
      finalBillAmount,
    });
  };

  const currentDoc = signedDocs[currentDocIndex] || {};

  if (!urlArray?.length) {
    return (
      <div className="cbc-empty" style={{ padding: 32 }}>
        <div>No documents available</div>
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <div className="cbc-two-col">
        {/* ── Left: File Viewer ── */}
        <div className="cbc-card">
          <div className="cbc-tabs">
            <button
              className={`cbc-tab ${activeFileTab === "pdf" ? "active" : ""}`}
              onClick={() => setActiveFileTab("pdf")}
            >
              View PDF
            </button>
            <button
              className={`cbc-tab ${activeFileTab === "attachments" ? "active" : ""}`}
              onClick={() => setActiveFileTab("attachments")}
            >
              Attachments ({(urlArray || []).length})
            </button>
          </div>

          {activeFileTab === "pdf" && (
            <div className="cbc-viewer">
              {pdfLoading ? (
                <div className="cbc-loading">
                  <CircularProgress size={28} />
                </div>
              ) : currentDoc.urlSign ? (
                currentDoc.isImage ? (
                  <img src={currentDoc.urlSign} alt="Document" />
                ) : (
                  <iframe src={currentDoc.urlSign} title="Document viewer" />
                )
              ) : (
                <div className="cbc-empty">Select a document to view</div>
              )}
            </div>
          )}

          {activeFileTab === "attachments" && (
            <div className="cbc-card-body">
              {(urlArray || []).map((item, i) => (
                <div className="cbc-doc-row" key={item.url + i}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>
                      {item.name || `Document ${i + 1}`}
                    </div>
                    {item.clickedBy && (
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>
                        {item.clickedBy}
                        {item.timestamp ? ` | ${item.timestamp}` : ""}
                      </div>
                    )}
                    {item.status && (
                      <span
                        className={`cbc-pill ${
                          item.status === "Approved"
                            ? "green"
                            : item.status === "Declined"
                            ? "red"
                            : "yellow"
                        }`}
                        style={{ marginTop: 4 }}
                      >
                        {item.status}
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      className="cbc-btn outline"
                      style={{ padding: "4px 8px", minHeight: 32 }}
                      onClick={() => {
                        setActiveFileTab("pdf");
                        loadDoc(i);
                      }}
                      title="View"
                    >
                      <RemoveRedEye sx={{ fontSize: 16 }} />
                    </button>
                    <button
                      className="cbc-btn outline"
                      style={{ padding: "4px 8px", minHeight: 32 }}
                      onClick={() => downloadAttachment(item)}
                      title="Download"
                    >
                      <CloudDownload sx={{ fontSize: 16 }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Right: Info + Sub-tabs ── */}
        <div className="cbc-card">
          {/* Patient info */}
          <div className="cbc-patient-strip" style={{ borderRadius: 0 }}>
            <div><span>Name: </span>{data?.name || data?.patient_name || "-"}</div>
            <div><span>City: </span>{data?.city || "-"}</div>
            <div><span>Hospital: </span>{data?.hospital || data?.hospital_name || "-"}</div>
            <div><span>Payment: </span>{paymentMode || "-"}</div>
          </div>

          {/* Sub-tabs */}
          <div className="cbc-tabs">
            <button
              className={`cbc-tab ${activeRightTab === "verification" ? "active" : ""}`}
              onClick={() => setActiveRightTab("verification")}
            >
              Verification & Action
            </button>
            {isFinalBill && (
              <>
                <button
                  className={`cbc-tab ${activeRightTab === "collection" ? "active" : ""}`}
                  onClick={() => setActiveRightTab("collection")}
                >
                  Collection Updation
                </button>
                <button
                  className={`cbc-tab ${activeRightTab === "history" ? "active" : ""}`}
                  onClick={() => setActiveRightTab("history")}
                >
                  History
                </button>
              </>
            )}
          </div>

          <div className="cbc-card-body">
            {activeRightTab === "verification" && (
              <VerificationPanel
                isFinalBill={isFinalBill}
                isCash={isCash}
                isDisable={isDisable}
                ipdNoOnBill={ipdNoOnBill}
                setIpdNoOnBill={setIpdNoOnBill}
                updateIPD={updateIPD}
                finalBillAmount={finalBillAmount}
                setFinalBillAmount={setFinalBillAmount}
                netBillAmount={netBillAmount}
                setNetBillAmount={setNetBillAmount}
                form={form}
                setForm={setForm}
                onFieldChange={onFieldChange}
                formatToDateTimeLocal={formatToDateTimeLocal}
                urlArray={urlArray}
                finalStatusArray={finalStatusArray}
                changeStatus={changeStatus}
                handleSaveData={handleSaveData}
              />
            )}
            {activeRightTab === "collection" && isFinalBill && (
              <CollectionUpdation
                appointmentId={appId}
                leadId={leadId}
                collectionStatus={
                  typeof data?.collectionStatus === "object"
                    ? data.collectionStatus?.status
                    : data?.collectionStatus
                }
                paymentMode={paymentMode}
              />
            )}
            {activeRightTab === "history" && isFinalBill && (
              <CollectionHistory appointmentId={appId} />
            )}
          </div>
        </div>
      </div>

      {/* Save Data button */}
      <div style={{ textAlign: "right", marginTop: 12 }}>
        <button
          className="cbc-btn primary full-mobile"
          onClick={handleSaveData}
          disabled={isDisable}
        >
          Save Data
        </button>
      </div>

      {/* Decline Reason Dialog */}
      <Dialog
        open={declineModalOpen}
        onClose={() => setDeclineModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Decline Reason
          <IconButton onClick={() => setDeclineModalOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <textarea
            className="cbc-input"
            rows={3}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="Enter reason for declining..."
            style={{ resize: "vertical", minHeight: 80 }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            <button className="cbc-btn outline" onClick={() => setDeclineModalOpen(false)}>
              Cancel
            </button>
            <button className="cbc-btn danger" onClick={confirmDecline}>
              Decline
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentActionsTab;

// ── Verification & Action sub-panel ──
function VerificationPanel({
  isFinalBill,
  isCash,
  isDisable,
  ipdNoOnBill,
  setIpdNoOnBill,
  updateIPD,
  finalBillAmount,
  setFinalBillAmount,
  netBillAmount,
  setNetBillAmount,
  form,
  setForm,
  onFieldChange,
  formatToDateTimeLocal,
  urlArray,
  finalStatusArray,
  changeStatus,
  handleSaveData,
}) {
  return (
    <div>
      {/* Bill Details */}
      {isFinalBill && (
        <>
          <div className="cbc-section-title">Bill Details</div>
          <div className="cbc-form-grid">
            <div>
              <label className="cbc-label">IPD No. on Bill</label>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  className="cbc-input"
                  value={ipdNoOnBill}
                  onChange={(e) => setIpdNoOnBill(e.target.value)}
                  disabled={isDisable}
                />
                <button
                  className="cbc-btn primary"
                  style={{ padding: "6px 12px", whiteSpace: "nowrap" }}
                  onClick={updateIPD}
                  disabled={isDisable}
                >
                  Update
                </button>
              </div>
            </div>
            {isCash && (
              <>
                <div>
                  <label className="cbc-label">Final Bill Amount *</label>
                  <input
                    type="number"
                    className="cbc-input"
                    value={finalBillAmount ?? ""}
                    onChange={(e) =>
                      setFinalBillAmount(e.target.value ? Number(e.target.value) : null)
                    }
                    disabled={isDisable}
                  />
                </div>
                <div>
                  <label className="cbc-label">Net Bill Amount</label>
                  <input
                    type="number"
                    className="cbc-input"
                    value={netBillAmount ?? ""}
                    onChange={(e) =>
                      setNetBillAmount(e.target.value ? Number(e.target.value) : null)
                    }
                    disabled={isDisable}
                  />
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Timings */}
      <div className="cbc-section-title">Admission, Discharge & OT Timings</div>
      <div className="cbc-form-grid">
        {[
          { key: "actual_admission_date", label: "Admission Date" },
          { key: "discharge_date", label: "Discharge Date" },
          { key: "actualOtStartTime", label: "OT Start Time" },
          { key: "actualOtEndTime", label: "OT End Time" },
        ].map(({ key, label }) => (
          <div key={key}>
            <label className="cbc-label">{label}</label>
            <input
              type="datetime-local"
              className="cbc-input"
              value={formatToDateTimeLocal(form[key])}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, [key]: e.target.value }));
                onFieldChange(key, e.target.value);
              }}
              disabled={isDisable}
            />
          </div>
        ))}
      </div>

      {/* Collection Summary */}
      <div className="cbc-section-title">Collection Summary</div>
      <div className="cbc-form-grid">
        <div>
          <label className="cbc-label">Total Collectible at Admission</label>
          <input
            className="cbc-input"
            value={form.totalCollectibleAtAdmission}
            readOnly
            disabled
          />
        </div>
        <div>
          <label className="cbc-label">Total Collectible at Discharge</label>
          <input
            className="cbc-input"
            value={form.totalCollectibleAtDischarge}
            readOnly
            disabled
          />
        </div>
        <div>
          <label className="cbc-label">Total Collected at Admission</label>
          <input
            type="number"
            className="cbc-input"
            value={form.totalCollectedAtAdmission}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                totalCollectedAtAdmission: e.target.value,
              }))
            }
            onBlur={(e) => onFieldChange("totalCollectedAtAdmission", e.target.value)}
            disabled={isDisable}
          />
        </div>
        <div>
          <label className="cbc-label">Total Collected at Discharge</label>
          <input
            type="number"
            className="cbc-input"
            value={form.totalCollectedAtDischarge}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                totalCollectedAtDischarge: e.target.value,
              }))
            }
            onBlur={(e) => onFieldChange("totalCollectedAtDischarge", e.target.value)}
            disabled={isDisable}
          />
        </div>
      </div>

      {/* Document Decision */}
      <div className="cbc-section-title">Document Decision</div>
      {(urlArray || []).map((doc, i) => {
        const decision = finalStatusArray[i];
        return (
          <div
            className="cbc-doc-row"
            key={doc.url + i}
            style={{ alignItems: "center" }}
          >
            <div style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>
              {doc.name || `Document ${i + 1}`}
              {decision && (
                <span
                  className={`cbc-pill ${
                    decision.status === "Approved"
                      ? "green"
                      : decision.status === "Declined"
                      ? "red"
                      : "yellow"
                  }`}
                  style={{ marginLeft: 8 }}
                >
                  {decision.status}
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                className="cbc-btn success"
                style={{ padding: "4px 12px", minHeight: 32, fontSize: 12 }}
                onClick={() => changeStatus("Approved", doc._id, i)}
                disabled={isApproveDisabled(
                  { status: decision?.status, source: doc.source },
                  isDisable
                )}
                title="Approve"
              >
                <ThumbUp sx={{ fontSize: 14 }} /> Approve
              </button>
              <button
                className="cbc-btn danger"
                style={{ padding: "4px 12px", minHeight: 32, fontSize: 12 }}
                onClick={() => changeStatus("Declined", doc._id, i)}
                disabled={isDeclineDisabled(
                  { status: decision?.status, source: doc.source },
                  isDisable
                )}
                title="Decline"
              >
                <ThumbDown sx={{ fontSize: 14 }} /> Decline
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
